import { Plus, MessageSquare, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ChatbotSessionCard } from './ChatbotSessionCard'
import type { ChatbotSession } from '@/types/chatbot'
import { useState, useMemo } from 'react'

export interface ChatbotSessionListProps {
  /** List of sessions to display */
  sessions: ChatbotSession[]
  /** Currently selected session ID */
  selectedSessionId?: string
  /** Loading state */
  isLoading?: boolean
  /** Called when a session is selected */
  onSelectSession?: (sessionId: string) => void
  /** Called when the new session button is clicked */
  onNewSession?: () => void
  /** Called when a session delete is requested */
  onDeleteSession?: (sessionId: string) => void
  /** Called when a session's project is changed */
  onProjectChange?: (sessionId: string, projectId: string | null) => void
  /** Additional CSS classes */
  className?: string
}

/**
 * Chat-app style sidebar listing chatbot sessions.
 * Prominent "New Chat" CTA, search bar, message-preview cards.
 */
export function ChatbotSessionList({
  sessions,
  selectedSessionId,
  isLoading = false,
  onSelectSession,
  onNewSession,
  onDeleteSession,
  onProjectChange,
  className,
}: ChatbotSessionListProps) {
  const [searchQuery, setSearchQuery] = useState('')

  // Filter sessions based on search query
  const filteredSessions = useMemo(() => {
    if (!searchQuery.trim()) {
      return sessions
    }
    const query = searchQuery.toLowerCase()
    return sessions.filter(
      (session) =>
        session.title?.toLowerCase().includes(query) ||
        session.lastMessage?.toLowerCase().includes(query)
    )
  }, [sessions, searchQuery])

  // Sort by updatedAt descending (most recent first)
  const sortedSessions = useMemo(() => {
    return [...filteredSessions].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    )
  }, [filteredSessions])

  return (
    <div className={cn('flex flex-col h-full overflow-hidden bg-muted/30', className)} role="complementary" aria-label="Chat sessions">
      {/* Header — New Chat CTA + search */}
      <div className="p-3 space-y-2 flex-shrink-0">
        <Button
          onClick={onNewSession}
          disabled={isLoading}
          className="w-full gap-2 rounded-xl h-10 font-medium shadow-sm"
        >
          <Plus className="h-4 w-4" />
          New Chat
        </Button>

        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search chats…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9 rounded-lg bg-background/60 border-0 focus-visible:ring-1"
            aria-label="Search conversations"
          />
        </div>
      </div>

      {/* Session list */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="px-2 pb-2 space-y-0.5">
          {isLoading ? (
            // Loading skeleton
            <div className="space-y-1 px-1" role="status" aria-label="Loading sessions">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[68px] rounded-xl bg-muted/60 animate-pulse"
                  aria-hidden="true"
                />
              ))}
            </div>
          ) : sortedSessions.length === 0 ? (
            // Empty state
            <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
              <div className="rounded-2xl bg-primary/5 border border-primary/10 p-4 mb-4">
                <MessageSquare className="h-8 w-8 text-primary/60" />
              </div>
              {searchQuery ? (
                <>
                  <p className="font-medium text-sm">No results found</p>
                  <p className="text-xs mt-1">Try a different search term</p>
                </>
              ) : (
                <>
                  <p className="font-medium text-sm">No conversations yet</p>
                  <p className="text-xs mt-1">Start a new chat above</p>
                </>
              )}
            </div>
          ) : (
            // Session cards
            sortedSessions.map((session) => (
              <ChatbotSessionCard
                key={session.sessionId}
                session={session}
                isSelected={session.sessionId === selectedSessionId}
                onClick={() => onSelectSession?.(session.sessionId)}
                onDelete={onDeleteSession ? () => onDeleteSession(session.sessionId) : undefined}
                onProjectChange={onProjectChange ? (projectId) => onProjectChange(session.sessionId, projectId) : undefined}
              />
            ))
          )}
        </div>
      </ScrollArea>

      {/* Footer with session count */}
      {!isLoading && sessions.length > 0 && (
        <div className="px-3 py-2 text-[11px] text-muted-foreground/70 text-center">
          {filteredSessions.length === sessions.length ? (
            <span>{sessions.length} chat{sessions.length !== 1 ? 's' : ''}</span>
          ) : (
            <span>
              {filteredSessions.length} of {sessions.length} chat
              {sessions.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
